import { useListPagination } from '@/hooks/useListPagination'
import { ListPagination } from '@/components/ui/list-pagination'
import { changedInventoryFields, invalidateProductStock } from '@/utils/productThreshold'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useSortingState } from '@/hooks/useSortingState'
import { SortableTableHead } from '@/components/ui/sortable-table-head'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline'
import { CubeIcon } from '@heroicons/react/24/solid'
import { getProdotti, getCategorieAttive, deleteProdotto } from '@/api/products'
import { giacenzeProdottiApi } from '@/api/magazzino'
import { useToast } from '@/hooks/use-toast'
import { fetchAllPaginatedResults } from '@/utils/pagination'
import {
  STOCK_FILTER_OPTIONS,
  STOCK_FILTER_VALUES,
} from '@/utils/stockFilters'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import ProdottiTableSkeleton from '@/components/prodotti/ProdottiTableSkeleton'

/**
 * Pagina lista prodotti.
 * 
 * Features:
 * - Tabella prodotti con paginazione
 * - Filtri per categoria e stato
 * - Ricerca per nome/codice
 * - Azioni rapide (visualizza, modifica, elimina)
 */
export default function ProdottiList() {
  const [search, setSearch] = useState('')
  const [categoriaFilter, setCategoriaFilter] = useState('all')
  const [statoFilter, setStatoFilter] = useState('all')
  const [stockFilter, setStockFilter] = useState(STOCK_FILTER_VALUES.ALL)
  const [ordering, setOrdering] = useSortingState('prodotti', 'nome')
  const [stockDialog, setStockDialog] = useState({ open: false, prodotto: null, giacenza: null })
  const [stockForm, setStockForm] = useState({
    quantita_disponibile: '',
    soglia_minima: '10',
    ubicazione: '',
  })
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Query prodotti
  const { page, setPage } = useListPagination({ search, categoriaFilter, statoFilter, stockFilter, ordering })

  const { data: prodottiData, isLoading, isFetching, error } = useQuery({
    queryKey: ['prodotti', { page, stockFilter, search, categoria: categoriaFilter, stato: statoFilter, ordering }],
    queryFn: () => {
      const params = { ordering, page, stock_filter: stockFilter }
      if (search) params.search = search
      if (categoriaFilter !== 'all') params.categoria = categoriaFilter
      if (statoFilter !== 'all') params.stato = statoFilter
      return getProdotti(params)
    },
  })

  // Query categorie per filtro
  const { data: categorieData } = useQuery({
    queryKey: ['categorie-attive'],
    queryFn: getCategorieAttive,
  })

  // Query giacenze prodotti (tutte le pagine) per mostrare stock nella stessa lista
  const { data: giacenzeData } = useQuery({
    queryKey: ['giacenze-prodotti', 'catalogo-all-pages'],
    queryFn: () => fetchAllPaginatedResults((params) => giacenzeProdottiApi.getAll(params)),
  })

  // Mutation per eliminare prodotto
  const deleteMutation = useMutation({
    mutationFn: deleteProdotto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prodotti'] })
      toast({
        title: 'Prodotto eliminato',
        description: 'Il prodotto è stato eliminato con successo.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Errore',
        description: error.response?.data?.detail || 'Impossibile eliminare il prodotto.',
        variant: 'destructive',
      })
    },
  })

  // Mutation modifica giacenza prodotto inline
  const updateStockMutation = useMutation({
    mutationFn: ({ giacenzaId, data }) => giacenzeProdottiApi.patch(giacenzaId, data),
    onSuccess: async () => {
      await invalidateProductStock(queryClient)
      toast({
        title: 'Giacenza aggiornata',
        description: 'La giacenza prodotto è stata aggiornata con successo.',
      })
      setStockDialog({ open: false, prodotto: null, giacenza: null })
    },
    onError: (error) => {
      const errorData = error.response?.data
      const firstError = errorData
        ? Object.values(errorData)[0]
        : null
      toast({
        title: 'Errore',
        description: Array.isArray(firstError) ? firstError[0] : (firstError || 'Impossibile aggiornare la giacenza.'),
        variant: 'destructive',
      })
    },
  })

  // Handler eliminazione con conferma
  const handleDelete = (prodotto) => {
    if (window.confirm(`Sei sicuro di voler eliminare "${prodotto.nome}"?`)) {
      deleteMutation.mutate(prodotto.id)
    }
  }

  const openStockDialog = (prodotto) => {
    const giacenza = giacenzeByProdotto.get(prodotto.id)
    if (!giacenza) {
      toast({
        title: 'Giacenza non disponibile',
        description: 'Per questo prodotto non è presente una giacenza modificabile in lista.',
        variant: 'destructive',
      })
      return
    }

    setStockForm({
      quantita_disponibile: String(giacenza.quantita_disponibile ?? 0),
      soglia_minima: String(giacenza.soglia_minima ?? 10),
      ubicazione: giacenza.ubicazione || '',
    })
    setStockDialog({ open: true, prodotto, giacenza })
  }

  const handleStockSubmit = () => {
    if (!stockDialog.giacenza) return

    let payload
    try {
      payload = changedInventoryFields(stockForm, stockDialog.giacenza)
    } catch (error) {
      toast({ title: 'Valore non valido', description: error.message, variant: 'destructive' })
      return
    }

    updateStockMutation.mutate({
      giacenzaId: stockDialog.giacenza.id,
      data: payload,
    })
  }

  // DRF può restituire array diretto o oggetto paginato con results
  const prodotti = Array.isArray(prodottiData?.data) 
    ? prodottiData.data 
    : (prodottiData?.data?.results || [])
  const categorie = Array.isArray(categorieData?.data)
    ? categorieData.data
    : (categorieData?.data?.results || [])
  const giacenze = Array.isArray(giacenzeData) ? giacenzeData : []
  const giacenzeByProdotto = new Map(
    giacenze.map((g) => [g.prodotto, g])
  )
  const prodottiFiltrati = prodotti
  const hasActiveFilters = Boolean(
    search || categoriaFilter !== 'all' || statoFilter !== 'all' || stockFilter !== STOCK_FILTER_VALUES.ALL
  )

  const handleResetFilters = () => {
    setSearch('')
    setCategoriaFilter('all')
    setStatoFilter('all')
    setStockFilter(STOCK_FILTER_VALUES.ALL)
  }

  // Formatta prezzo in euro
  const formatPrezzo = (prezzo) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(prezzo)
  }

  // Badge stato
  const getStatoBadge = (stato) => {
    const variants = {
      attivo: { variant: 'success', label: 'Attivo' },
      inattivo: { variant: 'warning', label: 'Inattivo' },
      eliminato: { variant: 'destructive', label: 'Eliminato' },
    }
    const config = variants[stato] || { variant: 'secondary', label: stato }
    return <Badge variant={config.variant}>{config.label}</Badge>
  }

  const getStockBadge = (giacenza) => {
    if (!giacenza) return <Badge variant="secondary">N/D</Badge>
    if (giacenza.is_sottoscorta) return <Badge variant="destructive">Sottoscorta</Badge>
    return <Badge variant="success">OK</Badge>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <CubeIcon className="h-8 w-8 text-primary" />
            Prodotti
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestisci il catalogo prodotti
          </p>
        </div>
        <Button asChild className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700">
          <Link to="/prodotti/nuovo">
            <PlusIcon className="h-5 w-5 mr-2" />
            Nuovo Prodotto
          </Link>
        </Button>
      </div>

      {/* Filtri */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-muted-foreground" />
            Filtri
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-5">
            {/* Ricerca */}
            <div className="relative md:col-span-2">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per nome o codice..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filtro categoria */}
            <Select value={categoriaFilter} onValueChange={setCategoriaFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {categorie.map((cat) => (
                  <SelectItem key={cat.id} value={String(cat.id)}>
                    {cat.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Filtro stato */}
            <Select value={statoFilter} onValueChange={setStatoFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="attivo">Attivo</SelectItem>
                <SelectItem value="inattivo">Inattivo</SelectItem>
              </SelectContent>
            </Select>

            {/* Filtro stock operativo */}
            <Select value={stockFilter} onValueChange={setStockFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Stock" />
              </SelectTrigger>
              <SelectContent>
                {STOCK_FILTER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="md:col-span-5 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleResetFilters}
                disabled={!hasActiveFilters}
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Reset filtri
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabella Prodotti */}
      {isLoading ? (
        <ProdottiTableSkeleton rows={8} />
      ) : !prodottiFiltrati || prodottiFiltrati.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <CubeIcon className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium">Nessun prodotto trovato</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {search || categoriaFilter !== 'all' || statoFilter !== 'all' || stockFilter !== STOCK_FILTER_VALUES.ALL
                  ? 'Prova a modificare i filtri di ricerca'
                  : 'Inizia aggiungendo il primo prodotto'}
              </p>
              {!search && categoriaFilter === 'all' && statoFilter === 'all' && stockFilter === STOCK_FILTER_VALUES.ALL && (
                <Button asChild className="mt-4">
                  <Link to="/prodotti/nuovo">
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Aggiungi Prodotto
                  </Link>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16"></TableHead>
                  <SortableTableHead field="codice" currentOrdering={ordering} onSort={setOrdering}>
                    Codice
                  </SortableTableHead>
                  <SortableTableHead field="nome" currentOrdering={ordering} onSort={setOrdering}>
                    Nome
                  </SortableTableHead>
                  <SortableTableHead field="categoria__nome" currentOrdering={ordering} onSort={setOrdering}>
                    Categoria
                  </SortableTableHead>
                  <SortableTableHead field="prezzo_vendita_cents" currentOrdering={ordering} onSort={setOrdering}>
                    Prezzo
                  </SortableTableHead>
                  <SortableTableHead field="margine_percentuale" currentOrdering={ordering} onSort={setOrdering}>Margine</SortableTableHead>
                  <SortableTableHead field="giacenza_disponibile" currentOrdering={ordering} onSort={setOrdering} className="text-center">Disp</SortableTableHead>
                  <SortableTableHead field="giacenza_riservata" currentOrdering={ordering} onSort={setOrdering} className="text-center">Ris</SortableTableHead>
                  <SortableTableHead field="giacenza_effettiva" currentOrdering={ordering} onSort={setOrdering} className="text-center">Eff</SortableTableHead>
                  <SortableTableHead field="soglia_minima" currentOrdering={ordering} onSort={setOrdering} className="text-center">Soglia</SortableTableHead>
                  <SortableTableHead field="stock_status" currentOrdering={ordering} onSort={setOrdering} className="text-center">Stock</SortableTableHead>
                  <SortableTableHead field="stato" currentOrdering={ordering} onSort={setOrdering}>Stato</SortableTableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {prodottiFiltrati.map((prodotto) => {
                  const giacenza = giacenzeByProdotto.get(prodotto.id)
                  return (
                    <TableRow key={prodotto.id}>
                    <TableCell>
                      {prodotto.immagine_url ? (
                        <img 
                          src={prodotto.immagine_url} 
                          alt={prodotto.nome}
                          className="w-10 h-10 object-cover rounded"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-muted rounded flex items-center justify-center">
                          <CubeIcon className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{prodotto.codice}</TableCell>
                    <TableCell>
                      <p className="font-medium">{prodotto.nome}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{prodotto.categoria_nome}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatPrezzo(prodotto.prezzo_vendita)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={prodotto.margine_percentuale >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                        {prodotto.margine_percentuale?.toFixed(1)}%
                      </span>
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      {giacenza ? giacenza.quantita_disponibile : '-'}
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground">
                      {giacenza ? giacenza.quantita_riservata : '-'}
                    </TableCell>
                    <TableCell className="text-center font-semibold">
                      {giacenza ? giacenza.quantita_effettiva : '-'}
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground">
                      {giacenza ? giacenza.soglia_minima : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {getStockBadge(giacenza)}
                    </TableCell>
                    <TableCell>
                      {getStatoBadge(prodotto.stato)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" title="Modifica prodotto" aria-label="Modifica prodotto" asChild>
                          <Link to={`/prodotti/${prodotto.id}/modifica`}>
                            <PencilSquareIcon className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Modifica giacenza"
                          aria-label="Modifica giacenza"
                          onClick={() => openStockDialog(prodotto)}
                        >
                          <BuildingStorefrontIcon className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-red-600 hover:text-red-700"
                          title="Elimina prodotto"
                          aria-label="Elimina prodotto"
                          onClick={() => handleDelete(prodotto)}
                          disabled={deleteMutation.isPending}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={stockDialog.open}
        onOpenChange={(open) => setStockDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Giacenza Prodotto</DialogTitle>
            <DialogDescription>
              Aggiorna rapidamente la giacenza di {stockDialog.prodotto?.nome}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="stock-prodotto-quantita">Quantità disponibile</Label>
              <Input
                id="stock-prodotto-quantita"
                type="number"
                value={stockForm.quantita_disponibile}
                onChange={(e) => setStockForm((prev) => ({ ...prev, quantita_disponibile: e.target.value }))}
                placeholder="Quantità disponibile"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="stock-prodotto-soglia">Soglia minima</Label>
              <Input
                id="stock-prodotto-soglia"
                type="number"
                min="0"
                value={stockForm.soglia_minima}
                onChange={(e) => setStockForm((prev) => ({ ...prev, soglia_minima: e.target.value }))}
                placeholder="Soglia minima"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="stock-prodotto-ubicazione">Ubicazione</Label>
              <Input
                id="stock-prodotto-ubicazione"
                value={stockForm.ubicazione}
                onChange={(e) => setStockForm((prev) => ({ ...prev, ubicazione: e.target.value }))}
                placeholder="Ubicazione (opzionale)"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setStockDialog({ open: false, prodotto: null, giacenza: null })}
            >
              Annulla
            </Button>
            <Button onClick={handleStockSubmit} disabled={updateStockMutation.isPending}>
              {updateStockMutation.isPending ? 'Salvataggio...' : 'Salva Giacenza'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ListPagination data={prodottiData?.data} page={page} setPage={setPage} isFetching={isFetching} error={error} />
    </div>
  )
}
